import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const CareTeamPanel = () => {
  const [careTeams, setCareTeams] = useState([
    {
      id: 1,
      patientName: 'Sarah Johnson',
      mrn: 'MRN-2024-789456',
      primaryProvider: 'Dr. Michael Chen',
      teamMembers: [
        { name: 'Dr. Michael Chen', role: 'Attending Physician', status: 'online', department: 'Cardiology' },
        { name: 'Nurse Emily Roberts', role: 'Primary Nurse', status: 'online', department: 'ICU' },
        { name: 'Dr. Sarah Williams', role: 'Cardiologist', status: 'away', department: 'Cardiology' },
        { name: 'John Martinez', role: 'Physical Therapist', status: 'offline', department: 'Rehabilitation' }
      ],
      urgentTasks: 2,
      lastUpdate: '15 min ago'
    },
    {
      id: 2,
      patientName: 'Robert Anderson',
      mrn: 'MRN-2024-456123',
      primaryProvider: 'Dr. Lisa Thompson',
      teamMembers: [
        { name: 'Dr. Lisa Thompson', role: 'Attending Physician', status: 'online', department: 'Internal Medicine' },
        { name: 'Nurse David Kim', role: 'Primary Nurse', status: 'online', department: 'Med-Surg' },
        { name: 'Dr. James Wilson', role: 'Nephrologist', status: 'online', department: 'Nephrology' }
      ],
      urgentTasks: 0,
      lastUpdate: '1 hour ago'
    }
  ]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'bg-success';
      case 'away':
        return 'bg-warning';
      case 'offline':
        return 'bg-muted';
      default:
        return 'bg-muted';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-1">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Users" size={20} className="text-primary" />
            Active Care Teams
          </h2>
          <Button variant="ghost" size="sm" iconName="RefreshCw">
            Refresh
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
        {careTeams?.map((team) => (
          <div
            key={team?.id}
            className="bg-background rounded-lg border border-border p-4 hover:shadow-elevation-2 transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">{team?.patientName}</h3>
                <p className="text-sm text-muted-foreground">{team?.mrn}</p>
              </div>
              {team?.urgentTasks > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-error/10 text-error text-xs font-medium">
                  <Icon name="AlertCircle" size={12} />
                  {team?.urgentTasks} Urgent
                </span>
              )}
            </div>

            <div className="space-y-2 mb-3">
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Stethoscope" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">Primary:</span>
                <span className="font-medium text-foreground">{team?.primaryProvider}</span>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Team Members</p>
              {team?.teamMembers?.map((member, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors"
                >
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary">
                        {member?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div className={cn(
                      "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card",
                      getStatusColor(member?.status)
                    )} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{member?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{member?.role} • {member?.department}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Updated {team?.lastUpdate}
              </span>
              <Button variant="ghost" size="xs" iconName="MessageSquare">
                Message Team
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CareTeamPanel;